/**
 * RoamerKnowledgeEngine.ts — Engine #9: the Roamer Knowledge Engine™.
 *
 * WorkRoamer's institutional memory. Unlike the Data Fusion Engine (live
 * retrieval), this engine STORES accumulated intelligence so the platform
 * gets smarter with every city, recommendation, mission, and contribution.
 *
 * Guarantees:
 *  • Versioning: updates never overwrite — the prior content is frozen into
 *    `history` and the version number bumps. Created/updated dates,
 *    verification status, and supporting evidence are first-class.
 *  • Confidence is DERIVED at read time (never stored) and every served item
 *    carries a mandatory four-part explanation (why / where / when / how).
 *  • Relationships form a typed graph (city → neighborhood → coworking → …)
 *    with intelligent navigation via getRelated().
 *  • Semantic search returns ranked KNOWLEDGE OBJECTS, not raw hits.
 *  • Consumers (Intelligence, Mission, Learning, Community, Predictive,
 *    Score, future AI assistant) only READ; the engine never acts on its own
 *    knowledge.
 *  • Deterministic injectable `now`; deep-clone at boundaries; sanitized
 *    persistence at nomad:knowledge:v1.
 */

import { buildExplanation, computeConfidence } from './confidence';
import type {
  CityKnowledgeProfile,
  KnowledgeEvidence,
  KnowledgeItem,
  KnowledgeRecord,
  KnowledgeRelation,
  KnowledgeRelationKind,
  KnowledgeSearchResult,
  KnowledgeSource,
  KnowledgeType,
  KnowledgeVerificationStatus,
} from './models';
import { expandTerms, extractBudgetUsd, scoreRecord, tokenize } from './search';
import { quotaSafeStorage } from '@/services/storage/QuotaSafeStorage';

const STORAGE_KEY = 'nomad:knowledge:v1';
quotaSafeStorage.registerManagedKey(STORAGE_KEY, 'Knowledge Engine');
const MAX_RECORDS = 10000;
const MAX_HISTORY_PER_RECORD = 25;

const KNOWLEDGE_TYPES: KnowledgeType[] = [
  'city', 'neighborhood', 'coworking', 'cafe', 'restaurant', 'housing',
  'transportation', 'internetProvider', 'mobileCarrier', 'safety', 'visa',
  'currency', 'utilities', 'hiddenGem', 'travelTip', 'missionTemplate',
  'roamerReport', 'bestPractice', 'faq',
];
const KNOWLEDGE_SOURCES: KnowledgeSource[] = [
  'intelligenceEngine', 'learningEngine', 'communityEngine', 'missionEngine',
  'scoreEngine', 'roamerLocal', 'verifiedContribution', 'userPreference',
  'missionHistory', 'travelHistory', 'roamerReport', 'externalSummary',
];
const VERIFICATIONS: KnowledgeVerificationStatus[] = ['verified', 'unverified', 'disputed', 'stale'];
const RELATION_KINDS: KnowledgeRelationKind[] = ['locatedIn', 'near', 'partOf', 'servedBy', 'relatedTo'];

// ─── Input shapes ──────────────────────────────────────────────────────────────

export interface KnowledgeInput {
  /** Stable id per real-world thing so repeat learnings version, not duplicate. */
  id: string;
  type: KnowledgeType;
  title: string;
  locationId?: string | null;
  facts?: Record<string, string | number | boolean>;
  tags?: string[];
  text?: string | null;
  source: KnowledgeSource;
  verification?: KnowledgeVerificationStatus;
}

type ChangeListener = () => void;

interface PersistedState {
  records: KnowledgeRecord[];
  relations: KnowledgeRelation[];
}

// ─── Sanitizers (corrupt persisted state must never poison the graph) ─────────

function sanitizeEvidence(raw: unknown): KnowledgeEvidence | null {
  if (raw == null || typeof raw !== 'object') return null;
  const e = raw as Record<string, unknown>;
  if (!KNOWLEDGE_SOURCES.includes(e.source as KnowledgeSource)) return null;
  if (typeof e.description !== 'string' || typeof e.at !== 'number') return null;
  return {
    source: e.source as KnowledgeSource,
    description: e.description,
    verifiedCount: typeof e.verifiedCount === 'number' ? Math.max(0, e.verifiedCount) : undefined,
    providerReliability: typeof e.providerReliability === 'number'
      ? Math.max(0, Math.min(1, e.providerReliability)) : 0.5,
    at: e.at,
  };
}

function sanitizeRecord(raw: unknown): KnowledgeRecord | null {
  if (raw == null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || r.id.length === 0) return null;
  if (!KNOWLEDGE_TYPES.includes(r.type as KnowledgeType)) return null;
  if (typeof r.title !== 'string') return null;
  if (!KNOWLEDGE_SOURCES.includes(r.source as KnowledgeSource)) return null;
  if (typeof r.createdAt !== 'number' || typeof r.updatedAt !== 'number') return null;
  return {
    id: r.id,
    type: r.type as KnowledgeType,
    title: r.title,
    locationId: typeof r.locationId === 'string' ? r.locationId : null,
    facts: r.facts != null && typeof r.facts === 'object' && !Array.isArray(r.facts)
      ? Object.fromEntries(Object.entries(r.facts as Record<string, unknown>)
          .filter(([, v]) => ['string', 'number', 'boolean'].includes(typeof v))) as KnowledgeRecord['facts']
      : {},
    tags: Array.isArray(r.tags) ? r.tags.filter((t): t is string => typeof t === 'string') : [],
    text: typeof r.text === 'string' ? r.text : null,
    source: r.source as KnowledgeSource,
    verification: VERIFICATIONS.includes(r.verification as KnowledgeVerificationStatus)
      ? (r.verification as KnowledgeVerificationStatus) : 'unverified',
    evidence: Array.isArray(r.evidence)
      ? r.evidence.map(sanitizeEvidence).filter((e): e is KnowledgeEvidence => e != null) : [],
    version: typeof r.version === 'number' && r.version >= 1 ? Math.floor(r.version) : 1,
    history: Array.isArray(r.history)
      ? (r.history as Record<string, unknown>[])
          .filter((h) => h != null && typeof h === 'object' && typeof h.version === 'number')
          .map((h) => ({
            version: h.version as number,
            title: typeof h.title === 'string' ? h.title : '',
            locationId: typeof h.locationId === 'string' ? h.locationId : null,
            facts: h.facts != null && typeof h.facts === 'object' && !Array.isArray(h.facts)
              ? (h.facts as KnowledgeRecord['facts']) : {},
            tags: Array.isArray(h.tags) ? h.tags.filter((t): t is string => typeof t === 'string') : [],
            text: typeof h.text === 'string' ? h.text : null,
            source: KNOWLEDGE_SOURCES.includes(h.source as KnowledgeSource)
              ? (h.source as KnowledgeSource) : (r.source as KnowledgeSource),
            verification: VERIFICATIONS.includes(h.verification as KnowledgeVerificationStatus)
              ? (h.verification as KnowledgeVerificationStatus) : 'unverified',
            evidence: Array.isArray(h.evidence)
              ? h.evidence.map(sanitizeEvidence).filter((e): e is KnowledgeEvidence => e != null) : [],
            lastValidatedAt: typeof h.lastValidatedAt === 'number' ? h.lastValidatedAt : (typeof h.at === 'number' ? h.at : r.updatedAt as number),
            at: typeof h.at === 'number' ? h.at : r.updatedAt as number,
          }))
          .slice(-MAX_HISTORY_PER_RECORD)
      : [],
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
    lastValidatedAt: typeof r.lastValidatedAt === 'number' ? r.lastValidatedAt : r.updatedAt,
  };
}

function sanitizeRelation(raw: unknown, hasId: (id: string) => boolean): KnowledgeRelation | null {
  if (raw == null || typeof raw !== 'object') return null;
  const rel = raw as Record<string, unknown>;
  if (typeof rel.fromId !== 'string' || typeof rel.toId !== 'string') return null;
  if (!RELATION_KINDS.includes(rel.kind as KnowledgeRelationKind)) return null;
  if (!hasId(rel.fromId) || !hasId(rel.toId)) return null;
  return { fromId: rel.fromId, toId: rel.toId, kind: rel.kind as KnowledgeRelationKind };
}

// ─── Engine ────────────────────────────────────────────────────────────────────

export class RoamerKnowledgeEngine {
  private records = new Map<string, KnowledgeRecord>();
  private relations: KnowledgeRelation[] = [];
  private listeners = new Set<ChangeListener>();

  constructor() {
    this.load();
  }

  // ── Accumulation (write path — sources feed knowledge in) ──────────────────

  /**
   * Create or version a knowledge record. Same id ⇒ the previous content is
   * frozen into history and the version bumps — knowledge accumulates,
   * it is never silently overwritten.
   */
  learn(input: KnowledgeInput, now: number = Date.now()): KnowledgeItem {
    const existing = this.records.get(input.id);
    if (existing) {
      // Full snapshot of every mutable field — prior state stays recoverable.
      existing.history.push({
        version: existing.version,
        title: existing.title,
        locationId: existing.locationId,
        facts: structuredClone(existing.facts),
        tags: [...existing.tags],
        text: existing.text,
        source: existing.source,
        verification: existing.verification,
        evidence: structuredClone(existing.evidence),
        lastValidatedAt: existing.lastValidatedAt,
        at: existing.updatedAt,
      });
      if (existing.history.length > MAX_HISTORY_PER_RECORD) {
        existing.history = existing.history.slice(-MAX_HISTORY_PER_RECORD);
      }
      existing.version += 1;
      existing.title = input.title;
      existing.facts = { ...existing.facts, ...(input.facts ?? {}) };
      if (input.tags) existing.tags = [...new Set([...existing.tags, ...input.tags])];
      if (input.text !== undefined) existing.text = input.text;
      existing.source = input.source;
      if (input.verification) existing.verification = input.verification;
      if (input.locationId !== undefined) existing.locationId = input.locationId ?? null;
      existing.updatedAt = now;
      this.persist();
      this.notify();
      return this.toItem(existing, now);
    }
    const record: KnowledgeRecord = {
      id: input.id,
      type: input.type,
      title: input.title,
      locationId: input.locationId ?? null,
      facts: { ...(input.facts ?? {}) },
      tags: [...new Set(input.tags ?? [])],
      text: input.text ?? null,
      source: input.source,
      verification: input.verification ?? 'unverified',
      evidence: [],
      version: 1,
      history: [],
      createdAt: now,
      updatedAt: now,
      lastValidatedAt: now,
    };
    this.records.set(record.id, record);
    this.enforceCap();
    this.persist();
    this.notify();
    return this.toItem(record, now);
  }

  /** Attach supporting evidence — strengthens confidence without a new version. */
  addEvidence(recordId: string, evidence: Omit<KnowledgeEvidence, 'at'>, now: number = Date.now()): boolean {
    const record = this.records.get(recordId);
    if (!record) return false;
    record.evidence.push({
      ...evidence,
      providerReliability: Math.max(0, Math.min(1, evidence.providerReliability)),
      at: now,
    });
    this.persist();
    this.notify();
    return true;
  }

  /** Mark a record as validated now (freshness resets; status updates). */
  validate(recordId: string, status: KnowledgeVerificationStatus, now: number = Date.now()): boolean {
    const record = this.records.get(recordId);
    if (!record) return false;
    record.verification = status;
    record.lastValidatedAt = now;
    this.persist();
    this.notify();
    return true;
  }

  // ── Relationships ────────────────────────────────────────────────────────────

  relate(fromId: string, kind: KnowledgeRelationKind, toId: string): boolean {
    if (!this.records.has(fromId) || !this.records.has(toId) || fromId === toId) return false;
    const exists = this.relations.some((r) => r.fromId === fromId && r.toId === toId && r.kind === kind);
    if (!exists) {
      this.relations.push({ fromId, toId, kind });
      this.persist();
      this.notify();
    }
    return true;
  }

  /** Directly related knowledge (both directions), optionally filtered by kind. */
  getRelated(recordId: string, kind?: KnowledgeRelationKind, now: number = Date.now()): KnowledgeItem[] {
    const ids = new Set<string>();
    for (const rel of this.relations) {
      if (kind && rel.kind !== kind) continue;
      if (rel.fromId === recordId) ids.add(rel.toId);
      else if (rel.toId === recordId) ids.add(rel.fromId);
    }
    return [...ids]
      .map((id) => this.records.get(id))
      .filter((r): r is KnowledgeRecord => r != null)
      .map((r) => this.toItem(r, now));
  }

  /**
   * Intelligent navigation: breadth-first walk of the relationship graph from
   * a record (city → neighborhood → coworking → café → …) up to `depth` hops.
   */
  navigate(recordId: string, depth: number = 2, now: number = Date.now()): KnowledgeItem[] {
    const visited = new Set<string>([recordId]);
    let frontier = [recordId];
    const found: string[] = [];
    for (let d = 0; d < depth && frontier.length > 0; d++) {
      const next: string[] = [];
      for (const id of frontier) {
        for (const rel of this.relations) {
          const neighbor = rel.fromId === id ? rel.toId : rel.toId === id ? rel.fromId : null;
          if (neighbor && !visited.has(neighbor)) {
            visited.add(neighbor);
            found.push(neighbor);
            next.push(neighbor);
          }
        }
      }
      frontier = next;
    }
    return found
      .map((id) => this.records.get(id))
      .filter((r): r is KnowledgeRecord => r != null)
      .map((r) => this.toItem(r, now));
  }

  // ── Retrieval (read path — consumers only read) ─────────────────────────────

  get(recordId: string, now: number = Date.now()): KnowledgeItem | null {
    const record = this.records.get(recordId);
    return record ? this.toItem(record, now) : null;
  }

  getByType(type: KnowledgeType, now: number = Date.now()): KnowledgeItem[] {
    return [...this.records.values()]
      .filter((r) => r.type === type)
      .map((r) => this.toItem(r, now))
      .sort((a, b) => b.confidence.score - a.confidence.score);
  }

  /** Semantic search: ranked knowledge objects, never raw hits. */
  search(query: string, now: number = Date.now(), limit: number = 20): KnowledgeSearchResult[] {
    const terms = expandTerms(tokenize(query));
    const budget = extractBudgetUsd(query);
    const results: KnowledgeSearchResult[] = [];
    for (const record of this.records.values()) {
      const hit = scoreRecord(record, terms, budget);
      if (!hit) continue;
      results.push({ item: this.toItem(record, now), relevance: hit.relevance, matchedTerms: hit.matchedTerms });
    }
    return results
      .sort((a, b) => b.relevance - a.relevance || b.item.confidence.score - a.item.confidence.score)
      .slice(0, limit);
  }

  /** Evolving city profile — improves as more knowledge accumulates. */
  getCityProfile(locationId: string, now: number = Date.now()): CityKnowledgeProfile {
    const records = [...this.records.values()].filter(
      (r) => r.locationId === locationId || (r.type === 'city' && r.id === locationId),
    );
    const sections: CityKnowledgeProfile['sections'] = {};
    let cityRecord: KnowledgeItem | null = null;
    let confidenceSum = 0;
    for (const record of records) {
      const item = this.toItem(record, now);
      confidenceSum += item.confidence.score;
      if (record.type === 'city') {
        cityRecord = item;
        continue;
      }
      (sections[record.type] ??= []).push(item);
    }
    for (const list of Object.values(sections)) {
      list!.sort((a, b) => b.confidence.score - a.confidence.score);
    }
    return {
      locationId,
      cityRecord,
      sections,
      recordCount: records.length,
      overallConfidence: records.length > 0 ? Math.round(confidenceSum / records.length) : 0,
      updatedAt: records.reduce((max, r) => Math.max(max, r.updatedAt), 0),
    };
  }

  getStats(): { recordCount: number; relationCount: number } {
    return { recordCount: this.records.size, relationCount: this.relations.length };
  }

  onChange(listener: ChangeListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // ── Internals ────────────────────────────────────────────────────────────────

  private toItem(record: KnowledgeRecord, now: number): KnowledgeItem {
    const clone = structuredClone(record);
    const confidence = computeConfidence(clone, now);
    return { record: clone, confidence, explanation: buildExplanation(clone, confidence, now) };
  }

  private notify(): void {
    this.listeners.forEach((l) => {
      try { l(); } catch { /* one broken subscriber never breaks the engine */ }
    });
  }

  private enforceCap(): void {
    if (this.records.size <= MAX_RECORDS) return;
    // Evict oldest-updated first.
    const sorted = [...this.records.values()].sort((a, b) => a.updatedAt - b.updatedAt);
    for (const record of sorted.slice(0, this.records.size - MAX_RECORDS)) {
      this.records.delete(record.id);
      this.relations = this.relations.filter((r) => r.fromId !== record.id && r.toId !== record.id);
    }
  }

  private persist(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const state: PersistedState = { records: [...this.records.values()], relations: this.relations };
      // BETA GATE HARDENING™: quota-safe write — evicts oldest-updated records
      // (and their relations) first under quota pressure; in-memory unchanged.
      quotaSafeStorage.writeJSON(STORAGE_KEY, state, (s, level) => {
        const keep = level === 1 ? 5000 : 2000;
        const records = [...s.records].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, keep);
        const ids = new Set(records.map((r) => r.id));
        return { records, relations: s.relations.filter((r) => ids.has(r.fromId) && ids.has(r.toId)) };
      });
    } catch { /* storage unavailable — keep working in memory */ }
  }

  private load(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const state = JSON.parse(raw) as Partial<PersistedState> | null;
      if (state == null || typeof state !== 'object') return;
      for (const rawRecord of Array.isArray(state.records) ? state.records : []) {
        const record = sanitizeRecord(rawRecord);
        if (record) this.records.set(record.id, record);
      }
      const hasId = (id: string) => this.records.has(id);
      this.relations = (Array.isArray(state.relations) ? state.relations : [])
        .map((r) => sanitizeRelation(r, hasId))
        .filter((r): r is KnowledgeRelation => r != null);
      // Cap invariant must hold even for oversized persisted state.
      this.enforceCap();
    } catch { /* corrupt state — start fresh rather than crash */ }
  }
}

/** App-wide singleton — WorkRoamer's institutional memory. */
export const roamerKnowledgeEngine = new RoamerKnowledgeEngine();
